import "react-toastify/dist/ReactToastify.css";
import { config } from "../config/config";
import { ToastContainer, toast } from "react-toastify";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useState, useEffect } from "react";
import axios from "axios";

const TeamMemberProfileForm = ({ member }) => {
  const user = useSelector((state) => state?.user);

  const [errors, setErrors] = useState({});
  const [isEdited, setIsEdited] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [roles, setRoles] = useState([]);
  const [updatedMember, setUpdatedMember] = useState({
    email: member.email || "",
    firstname: member.firstname || "",
    lastname: member.lastname || "",
    role: member.role || "",
  });
  const { id } = useParams();
  const businessId = user.user?.businessId;
  const businessIdentifier = user.user?.businessIdentifier;
  const navigate = useNavigate();

  useEffect(() => {
    const hasChanges = Object.keys(updatedMember).some(
      (key) => updatedMember[key] !== member[key]
    );
    setIsEdited(hasChanges);
  }, [updatedMember, member]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUpdatedMember({
      ...updatedMember,
      [name]: value,
    });
    setIsEdited(true);
  };

  const validateEmail = (email) => {
    // Updated regex to enforce lowercase domain and allow valid TLDs (like .com, .org, .net)
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/;
    const reservedKeywords = ["email.com", "support@example.com"]; // Reserved keywords
    
    // Check if the email exceeds the general length limit (254 characters)
    if (email.length > 30) {
      return false;
    }
  
    // Check if the email matches the regex
    if (!emailRegex.test(email)) {
      return false;
    }
  
    // Split the email into the username and domain
    const [username, domain] = email.split("@");

    if (!username || !domain) {
      return false; // Missing username or domain
    }
  
    // Check for multiple dots or leading/trailing dots
    if (username.includes("..") || domain.includes("..") || username.startsWith(".") || username.endsWith(".") || domain.startsWith(".") || domain.endsWith(".")) {
      return false;
    }

    // Ensure the domain does not contain reserved keywords
    if (reservedKeywords.includes(domain)) {
      return false;
    }

    // Check if the domain contains a valid single TLD and nothing extra after that
    const domainParts = domain.split('.');
    const tld = domainParts[domainParts.length - 1]; // Get the last part (TLD)

    // Valid top-level domains (you can add more TLDs to this list)
    const validTLDs = ['com', 'org', 'net'];

    // If there are more than 2 parts (e.g. user@something.co.uk) or invalid TLD
    if (domainParts.length > 2 || !validTLDs.includes(tld)) {
      return false;
    }

    return true;
  }

  const validateForm = () => {
    const newErrors = {};

    if (!updatedMember.firstname || updatedMember.firstname.length > 30) {
      newErrors.first_name = "First name must be up to 30 characters long";
      toast.error(newErrors.first_name);
    }

    if (!updatedMember.lastname || updatedMember.lastname.length > 30) {
      newErrors.last_name = "Last name must be up to 30 characters long";
      toast.error(newErrors.last_name);
    }

    if (!validateEmail(updatedMember.email)) {
      newErrors.email = "Invalid email format";
      toast.error(newErrors.email);
    }

    if (!updatedMember.role) {
      newErrors.last_name = "Please select the role";
      toast.error(newErrors.last_name);
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const getRoles = async () => {
    try {
      const request = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/role/getRoleNames?businessId=${businessId}&businessIdentifier=${businessIdentifier}`,
        config
      );

      if (request.data.success) {
        setRoles(request.data.data);
      } else {
        toast.error("Could not retrieve roles.");
      }
    } catch (error) {
      console.error("🚀 -> getRoles -> error:", error);
    }
  };

  useEffect(() => {
    getRoles();
  }, []);

  const onSave = async (e) => {
    e.preventDefault();

    try {
      if (!isEdited) {
        toast.info(
          "No changes detected. Please update team member profile before saving."
        );
        return;
      }

      if (!isSaved && validateForm()) {
        const request = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/member/editMember/${id}`,
          updatedMember
        );

        if (request.data.success) {
          toast.success(request.data.message);
          setIsSaved(true);

          setTimeout(() => {
            navigate("/team-member");
          }, 3000);
        } else {
          toast.error(request.data.message);
        }
      }
    } catch (error) {
      if (!error.response) {
        toast.error(
          "Network connection lost. Please check your connection and try again."
        );
      } else {
        toast.error(
          error.response.data.message || "An unexpected error occurred."
        );
      }
    }
  };

  return (
    <div style={styles.container}>
      <ToastContainer />
      <div style={styles.header}>
        <h2 style={styles.title}>Update Team Member Profile</h2>
        <div style={styles.buttonContainer}>
          <button style={styles.editButton} onClick={onSave}>
            Save
          </button>
        </div>
      </div>
      <div style={styles.infoContainer}>
        <div style={styles.infoRow}>
          <span style={styles.span}>First Name</span>
          <input
            type="text"
            name="firstname"
            value={updatedMember.firstname}
            onChange={handleInputChange}
            style={styles.input}
          />
        </div>

        <div style={styles.infoRow}>
          <span style={styles.span}>Last Name</span>
          <input
            type="text"
            name="lastname"
            value={updatedMember.lastname}
            onChange={handleInputChange}
            style={styles.input}
          />
        </div>
        <div style={styles.infoRow}>
          <span style={styles.span}>Email</span>
          <input
            type="email"
            name="email"
            value={updatedMember.email}
            onChange={handleInputChange}
            style={styles.input}
          />
        </div>
        <div style={styles.infoRow}>
          <span style={styles.span}>Role</span>
          <select
            name="role"
            value={updatedMember.role}
            onChange={handleInputChange}
            style={styles.input}
          >
            <option value="">Select Role</option>
            {roles.map((role) => (
              <option key={role.id} value={role.id}>
                {role?.name}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    backgroundColor: "#fff",
    border: "0px solid #ccc",
    borderRadius: "8px",
    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
    fontFamily: "Poppins",
    overflow: "hidden",
    width: "1000px",
  },
  header: {
    alignItems: "center",
    backgroundColor: "#064260",
    color: "#fff",
    display: "flex",
    justifyContent: "space-between",
    padding: "20px 35px",
  },
  title: {
    fontSize: "21px",
    fontWeight: 600,
    margin: 0,
  },
  buttonContainer: {
    display: "flex",
    gap: "10px",
  },
  infoContainer: {
    border: "0",
    padding: "0",
  },
  infoRow: {
    borderBottom: "1px solid #eee",
    display: "flex",
    fontSize: "1em",
    justifyContent: "space-between",
    padding: "15px 35px",
  },
  span: {
    fontSize: "17px",
    fontWeight: 600,
    margin: 0,
  },
  input: {
    backgroundColor: "#F4F4F6",
    border: "none",
    borderRadius: "4px",
    color: "#808080",
    fontFamily: "Poppins",
    fontSize: "16px",
    padding: "8px",
    width: "60%",
  },
  editButton: {
    backgroundColor: "#27B99A",
    border: "none",
    borderRadius: "35px",
    color: "#fff",
    cursor: "pointer",
    fontSize: "10px",
    fontWeight: 500,
    padding: "8px 10px",
    width: "80px",
  },
};

export default TeamMemberProfileForm;
